import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, TreePine } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '/new-forest-caravan-holidays' || location.pathname === '/hoburne-park-caravan-hire';

  const dashboardLink = user && user.role === 'owner' ? '/admin' : user && user.role === 'manager' ? '/manager' : user && user.role === 'cleaner' ? '/cleaner' : null;

  const scrollTo = (id) => {
    setOpen(false);
    if (!isHome) {
      window.location.href = `/#${id}`;
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = [
    { label: 'The Caravan', id: 'caravan' },
    { label: 'Availability', id: 'availability' },
    { label: 'The Park', id: 'about' },
    { label: 'Reviews', id: 'reviews' },
  ];

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/80 border-b border-[#E6D5B8]/50" data-testid="main-navbar">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <TreePine className="w-6 h-6 text-[#1E3F33]" />
            <span className="font-heading text-xl md:text-2xl font-medium text-[#1E3F33] tracking-tight">
              New Forest Caravans
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} className="text-sm font-medium transition-colors hover:text-[#CC5A3D] text-[#1F2A25]" data-testid={`nav-${item.id}`}>
                {item.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to={dashboardLink || '/admin'}>
                <Button className="bg-[#1E3F33] text-[#F9F8F6] hover:bg-[#163026] rounded-sm px-5 text-sm" data-testid="nav-dashboard-btn">
                  {user.name}
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link">
                  <Button variant="ghost" className="text-xs text-[#6B726A] hover:bg-[#F0EBE1] rounded-sm">Staff</Button>
                </Link>
                <Button onClick={() => scrollTo('availability')} className="bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm px-5 text-sm" data-testid="nav-cta">
                  Book Now
                </Button>
              </>
            )}
          </div>

          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6 text-[#1F2A25]" /> : <Menu className="w-6 h-6 text-[#1F2A25]" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-6 border-t border-[#E6D5B8] pt-4 space-y-3">
            {navItems.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} className="block w-full text-left py-2 text-sm font-medium text-[#1F2A25]" data-testid={`mobile-nav-${item.id}`}>
                {item.label}
              </button>
            ))}
            {user ? (
              <Link to={dashboardLink || '/admin'} className="block py-2 text-sm font-medium text-[#CC5A3D]" onClick={() => setOpen(false)}>Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="block py-2 text-xs text-[#6B726A]" onClick={() => setOpen(false)}>Staff Login</Link>
                <Button onClick={() => scrollTo('availability')} className="w-full bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm mt-2">
                  Book Now
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
